import Vuex from 'vuex'
import Vue from 'vue'
import contract from './contract'
import invoice from './invoice'

Vue.use(Vuex)

export default new Vuex.Store({
    strict: false,
    modules: {
        contract,
        invoice
    },
    state: {
        // Base
        version: '1.69.1',
        authToken: null,
        initialized: false,
        auth_redirected: false,
        user: {},
        env: null,
        searchQueries: {},
        dataBelongsTo: 'all',
        dateRange: [],
        boxView: 'list',
        exporting: false,
        hideSensitiveData: true,
        showActive: true,
        showCancelledOrders: false,
        showPaidOrders: true,
        connect: {
            version: '1.20',
            date: 'Juni 2025',
            download: '/files/PlanitEasy-Connect-Setup-1.2.0.exe'
        },
        lastOpenedFloorplan: 0,
        selectedFilters: null,
        organisations: null,
    },

    actions: {
        setToken({commit, dispatch, state}, payload){
            console.log('[🔥] Setting authentication token!')

            return axios.post('/setauthtoken', {'token': payload}).then(() => {
                commit('setToken', payload)
            })
        },

        loadApp({commit, dispatch, state}){
            return new Promise((resolve) => {
                /**
                 * Get the authenticated user
                 */
                let getUser = this.dispatch('getUser')

                // Set daterange to this month.
                dispatch('setDateRange', null)

                /**
                 * All finished
                 */
                Promise.all([getUser]).then(() => {
                    setTimeout(() => {
                        commit('setInitialized', true)
                    }, 500)

                    resolve('loaded')
                }).catch((error) => {
                    console.error('[❌] Error while loading app:', error)
                })
            })
        },

        getUser({commit, dispatch, state}){
            return new Promise((resolve, reject) => {
                axios.get('/api/user', {
                    headers: {'Authorization': 'bearer ' + state.authToken}
                }).then((response) => {
                    commit('setUser', response.data.user)

                    if(_.has(response.data, 'organisations')){
                        // console.log('[🔥] Setting organisations:', response.data.organisations)
                        commit('setOrganisations', response.data.organisations)
                    }

                    dispatch('setDataBelongsTo', response.data.user.organisation)

                    resolve(true)
                }).catch((error) => {
                    console.error('[❌] Error while fetching user:', error)
                    reject(error)
                })
            })
        },

        switchOrganisation({commit, dispatch, state}, id){
            return new Promise((resolve) => {
                axios.post('/api/user/switchorganisation', {
                    id
                }, {
                    headers: {'Authorization': 'bearer ' + state.authToken}
                }).then(() => {
                    commit('emptySearchQueries') // Empty search queries

                    dispatch('loadApp').then(() => {
                        resolve()
                    })
                }).catch((error) => {
                    console.error('[❌] Error while switching organisation:', error)
                })
            })
        },

        saveUserSettings({commit}, settings) {
            commit('setUserSettings', settings)
        },

        fetchConfirmStops({commit, state}) {
            axios.get('/api/contract/fetchconfirmstops', {
                headers: {'Authorization': 'bearer ' + state.authToken}
            }).then((response) => {
                commit('setConfirmStops', response.data)
            })
        },

        fetchInvoicesNeedsAction({commit, state}) {
            axios.get('/api/invoices/needs-action', {
                headers: {'Authorization': 'bearer ' + state.authToken}
            }).then((response) => {
                commit('setInvoicesNeedsAction', response.data)
            })
        },

        setSearchQuery({commit}, payload){
            if(!_.isNull(payload.query) && !_.isNull(payload.type)){
                console.log('[🔥] Set search query for type: '+payload.type, payload.query)
            }

            if(!_.isNull(payload.type)){
                commit('setSearchQuery', payload)
            }
        },

        setDataBelongsTo({commit}, payload){
            commit('setDataBelongsTo', payload)
        },

        setDateRange({commit}, payload){
            commit('setDateRange', payload)
        },

        setBoxView({commit}, payload){
            commit('setBoxView', payload)
        },

        toggleSensitiveData({commit, dispatch, state}, payload){
            // If payload is set, use that value. Otherwise toggle the current value.
            commit('setSensitiveData', payload !== undefined ? payload : !state.hideSensitiveData)
        },

        // setupConnect() {
        //     window.Echo = new Echo({
        //         broadcaster: 'pusher',
        //         key: process.env.MIX_PUSHER_APP_KEY,
        //         // cluster: process.env.MIX_PUSHER_APP_CLUSTER,
        //         encrypted: false,
        //         wsHost: window.location.hostname,
        //         wsPort: 6001,
        //         wssPort: 6001,
        //         enabledTransports: ['ws', 'flash']
        //     })
        // },

        updateConnectVisibility({commit}, payload){
            commit('setConnect', payload)
        },

        setShowActive({commit}, payload) {
            commit('setShowActive', payload)
        },

        setAuthRedirected({commit}, value) {
            commit('setAuthRedirected', value)
        },

        setShowCancelledOrders({commit}, value) {
            commit('setShowCancelledOrders', value)
        },
        setShowPaidOrders({commit}, value) {
            commit('setShowPaidOrders', value)
        },

        setLastOpenedFloorplan({commit, state}, value) {
            commit('setLastOpenedFloorplan', value)

            // If the open_preference is 4, set the last_opened_floorplan to the value if not already set.
            if(_.has(state.user, 'settings.open_preference') && state.user.settings.open_preference == 4){
                axios.post('/api/user/save',  { settings: { ...state.user.settings, last_opened_floorplan: value }}, {
                    headers: {'Authorization': 'bearer ' + state.authToken}
                }).then(() => {
                    commit('setUserSettings', { ...state.user.settings, last_opened_floorplan: value })
                })
            }
        },

        setSelectedFilters({commit}, payload) {
            commit('setSelectedFilters', payload)
        }
    },

    mutations: {
        setToken(state, payload) {
            state.authToken = payload
        },

        setInitialized(state, payload){
            state.initialized = payload
        },

        setUser(state, payload){
            state.user = payload
        },

        setUserSettings(state, payload){
            state.user.settings = payload
        },

        setConfirmStops(state, payload) {
            state.user.confirm_stops = payload
        },

        setInvoicesNeedsAction(state, payload) {
            state.user.invoice_needs_action = payload
        },

        setAuthRedirected(state, payload) {
            state.auth_redirected = payload
        },

        setOrganisations(state, payload) {
            state.organisations = payload
        },

        emptySearchQueries(state) {
            state.searchQueries = {}
        },

        setSearchQuery(state, payload){
            state.searchQueries[payload.type] = payload.query
        },

        setEnvironment(state, payload){
            state.env = payload

            if(payload == 'demo'){
                state.hideSensitiveData = false
            }
        },

        startExport(state, payload){
            state.exporting = payload
        },

        setDataBelongsTo(state, payload){
            state.dataBelongsTo = payload
        },

        setDateRange(state, payload){
            state.dateRange = payload
        },

        setBoxView(state, payload){
            state.boxView = payload
        },

        setShowActive(state, payload){
            state.showActive = payload
        },

        setSensitiveData(state, payload){
            state.hideSensitiveData = payload
        },

        setConnect(state, payload){
            state.user.organisation_connect = payload
        },

        setShowCancelledOrders(state, payload){
            state.showCancelledOrders = payload
        },

        setShowPaidOrders(state, payload){
            state.showPaidOrders = payload
        },

        setLastOpenedFloorplan(state, payload){
            state.lastOpenedFloorplan = payload
        },

        setSelectedFilters(state, payload) {
            state.selectedFilters = payload
        }
    }
})